import React, { RefObject } from "react";
import {
  Box,
  Flex,
  Grid,
  GridItem,
  Text,
  Divider,
  ResponsiveValue,
  Radio,
} from "@chakra-ui/react";
import { PurchaseOrderViewModel } from "../../../../models/PurchaseOrder";
import { DateTime } from "luxon";
import { formatPaymentTerm } from "../../../../utils/paymentTerms";

const formatDate = (value?: string | null) => {
  const date = DateTime.fromISO(value ?? "");
  return date.isValid && date.year > 1 ? date.toFormat("dd/LL/yyyy") : "-";
};

interface PurchaseOrderPrintProps {
  printRef: RefObject<any>;
  purchaseOrder: PurchaseOrderViewModel;
}

const PurchaseOrderPrint: React.FC<PurchaseOrderPrintProps> = ({
  printRef,
  purchaseOrder,
}) => {
  // Mock data for demonstration - replace with actual purchaseOrder data
  const mockData = purchaseOrder;

  const TableCell: React.FC<{
    pt?: any;
    children?: React.ReactNode;
    rowSpan?: number;
    colSpan?: number;
    bg?: string;
    textAlign?: ResponsiveValue<CanvasTextAlign> | undefined;
    fontWeight?: string;
  }> = ({
    pt,
    children,
    colSpan = 1,
    rowSpan = 1,
    bg = "white",
    textAlign = "left",
    fontWeight = "normal",
  }) => (
    <GridItem
      rowSpan={rowSpan}
      colSpan={colSpan}
      border="1px solid black"
      p="2px"
      fontSize="12.5px"
      bg={bg}
      textAlign={textAlign}
      fontWeight={fontWeight}
      pt={pt}
    >
      <Text>{children}</Text>
    </GridItem>
  );

  const HeaderCell: React.FC<{
    children: React.ReactNode;
    colSpan?: number;
  }> = ({ children, colSpan = 1 }) => (
    <GridItem
      colSpan={colSpan}
      border="1px solid black"
      p="2px"
      fontSize="13px"
      fontWeight="semibold"
      bg="gray.100"
      textAlign="center"
    >
      <Text>{children}</Text>
    </GridItem>
  );

  const SignatureLine: React.FC<{ name: string }> = ({ name }) => (
    <Box textAlign="right" mt="80px" w="200px">
      <Divider borderWidth="1.3px" borderColor="black" mx="auto" mb="2px" />
      <Text fontSize="13px" fontFamily={"Arial"}>
        {name}
      </Text>
    </Box>
  );

  const formatterUsd = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "USD",
  });

  const formatterBrl = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

  return (
    <Box
      w="100%"
      maxW="4xl"
      mx="auto"
      bg="white"
      p="8px"
      fontFamily="mono"
      fontSize="14px"
      ref={printRef}
    >
      {/* Header with logo and company info */}
      <Flex justify="space-between" align="flex-start" mb="12px">
        <img
          src="grupoCvfLogo.png"
          alt="CVF Logo"
          style={{ width: "120px", height: "auto" }}
        />
        <Box textAlign="right" fontSize="13px">
          <Text>CVF DISTRIBUIDORA LTDA.</Text>
          <Text>RUA XAVANTES, 54</Text>
          <Text>JOINVILLE - SC - BRASIL - 89203-900</Text>
          <Text>CNPJ: 37.754.378/0001-90</Text>
        </Box>
      </Flex>

      {/* Main title */}
      <Grid
        templateColumns="repeat(4, 1fr)"
        gap={0}
        mb="0"
        borderBottom="0"
        border="1px solid black"
      >
        <HeaderCell colSpan={8}>
          PEDIDO DE COMPRA INTERNO - DISTRIBUIÇÃO
        </HeaderCell>
      </Grid>

      {/* Top section with date, PO number, etc. */}
      <Grid
        templateColumns="repeat(4, 1fr)"
        gap={0}
        border="1px solid black"
        borderTop="1px"
        borderBottom="1px"
      >
        <TableCell textAlign="center" bg="gray.200">
          {formatDate(mockData.createdDate)}
        </TableCell>
        <TableCell textAlign="center" bg="gray.200">
          PO CVF {mockData.poNumber}
        </TableCell>
        <TableCell textAlign="center" bg="gray.200" colSpan={2}>
          ALFA 754 - XXX
        </TableCell>
      </Grid>

      {/* Client, CNPJ, Destination section */}
      <Grid
        templateColumns="repeat(4, 1fr)"
        gap={0}
        mb="8px"
        border="1px solid black"
        borderTop={0}
      >
        <TableCell bg="gray.200">CLIENTE</TableCell>
        <TableCell colSpan={3}>{mockData.client}</TableCell>
        <TableCell bg="gray.200">CNPJ</TableCell>
        <TableCell>{mockData.cnpj}</TableCell>
        <TableCell bg="gray.200" textAlign="center">
          REGIME TRIBUTÁRIO
        </TableCell>
        <TableCell>{mockData.taxRegime || "-"}</TableCell>
        <TableCell bg="gray.200">DESTINAÇÃO</TableCell>
        <TableCell>{mockData.destinationPort}</TableCell>
        <TableCell bg="gray.200" textAlign="center">
          ST OU DIFAL
        </TableCell>
        <TableCell>{mockData.stOrDifalDestination || "-"}</TableCell>
      </Grid>
      {/* Invoice and supplier section */}

      <Grid
        templateColumns="repeat(4, 1fr)"
        gap={0}
        mb="8px"
        border="1px solid black"
      >
        <TableCell bg="gray.200">REF. INVOICE FORNECEDOR</TableCell>
        <TableCell>{mockData.invoiceRef}</TableCell>
        <TableCell bg="gray.200" textAlign="center">
          DATA INVOICE
        </TableCell>
        <TableCell>
          {formatDate(mockData.supplierInvoiceDate)}
        </TableCell>
        <TableCell bg="gray.200">FORNECEDOR</TableCell>
        <TableCell>{mockData.supplier}</TableCell>
        <TableCell bg="gray.200" textAlign="center">
          PORTO DE ORIGEM
        </TableCell>
        <TableCell>{mockData.originPort}</TableCell>
        <TableCell bg="gray.200">INCOTERM</TableCell>
        <TableCell>{mockData.incoterm}</TableCell>
        <TableCell bg="gray.200" textAlign="center">
          PORTO DE DESTINO
        </TableCell>
        <TableCell>{mockData.destinationPort}</TableCell>
        <TableCell bg="gray.200">PRODUTO - NCM</TableCell>
        <TableCell colSpan={3}>{mockData.productNcm}</TableCell>
        <TableCell bg="gray.200">QTE CONTAINER</TableCell>
        <TableCell colSpan={3}>{mockData.containerQty}</TableCell>
        <TableCell bg="gray.200">CUSTO FINANCEIRO</TableCell>
        <TableCell colSpan={3}>
          {mockData.financialCost != null
            ? formatterUsd.format(mockData.financialCost)
            : "-"}
        </TableCell>
        <TableCell bg="gray.200">MARGEM</TableCell>
        <TableCell>{mockData.margin}</TableCell>
        <TableCell bg="gray.200" textAlign="center">
          FAT. NACIONALIZAÇÃO
        </TableCell>
        <TableCell bg="gray.200" textAlign="center">
          VALOR FINAL NF
        </TableCell>
        <TableCell bg="gray.200">VALOR FOB TOTAL (PI)</TableCell>
        <TableCell>{formatterUsd.format(mockData.totalFobValue)}</TableCell>
        <TableCell>{mockData.nationalizationFactor ?? "-"}</TableCell>
        <TableCell>{formatterBrl.format(mockData.finalInvoiceValue)}</TableCell>
      </Grid>

      {/* Resource section */}
      <Grid
        templateColumns="repeat(4, 1fr)"
        gap={0}
        mb="8px"
        border="1px solid black"
      >
        <TableCell bg="gray.200" textAlign="center">
          ORIGEM RECURSO
        </TableCell>
        <TableCell colSpan={3}>
          <Flex justifyContent="space-around">
            <Flex justifyContent="center" gap={1}>
              <Radio
                size="sm"
                borderColor="black"
                isChecked={mockData.ownCash}
              />{" "}
              CAIXA PRÓPRIO
            </Flex>
            <Flex justifyContent="center" gap={1}>
              <Radio
                size="sm"
                borderColor="black"
                isChecked={mockData.reserveCash}
              />{" "}
              CAIXA RESERVA
            </Flex>
            <Flex justifyContent="center" gap={1}>
              <Radio
                size="sm"
                borderColor="black"
                isChecked={mockData.finimp}
              />{" "}
              FINIMP
            </Flex>

            <Flex justifyContent="center" gap={1}>
              <Radio
                size="sm"
                borderColor="black"
                isChecked={mockData.letterOfCredit}
              />{" "}
              LC
            </Flex>
          </Flex>
        </TableCell>
      </Grid>

      {/* Payment terms section */}
      <Grid
        templateColumns="repeat(4, 1fr)"
        gap={0}
        mb="0"
        borderBottom="0"
        border="1px solid black"
      >
        <HeaderCell colSpan={8}>TERMOS DE PAGAMENTO</HeaderCell>
      </Grid>

      <Grid
        templateColumns="repeat(4, 1fr)"
        gap={0}
        mb="8px"
        border="1px solid black"
        borderTop="0"
      >
        <TableCell bg="gray.200"></TableCell>
        <HeaderCell>1º CÂMBIO</HeaderCell>
        <HeaderCell>2º CÂMBIO</HeaderCell>
        <HeaderCell>RACIONALIZAÇÃO</HeaderCell>

        <TableCell bg="gray.200" rowSpan={2} textAlign="center" pt="12px">
          FORNECEDOR
        </TableCell>
        <TableCell bg="gray.200" textAlign="center">
          {formatPaymentTerm(
            purchaseOrder.firstExchangeSupplierTerm,
            purchaseOrder.firstExchangeSupplierTermDays
          )}
        </TableCell>
        <TableCell bg="gray.200" textAlign="center">
          {formatPaymentTerm(
            purchaseOrder.secondExchangeSupplierTerm,
            purchaseOrder.secondExchangeSupplierTermDays
          )}
        </TableCell>
        <TableCell bg="gray.200" textAlign="center">
          {formatPaymentTerm(
            purchaseOrder.nationalizationSupplierTerm,
            purchaseOrder.nationalizationSupplierTermDays
          )}
        </TableCell>
        <TableCell>
          {formatterUsd.format(mockData.firstExchangeSupplier)}
        </TableCell>
        <TableCell>
          {formatterUsd.format(mockData.secondExchangeSupplier)}
        </TableCell>
        <TableCell>
          {formatterUsd.format(mockData.nationalizationSupplier)}
        </TableCell>
        <TableCell bg="gray.200" rowSpan={2} textAlign="center" pt="12px">
          CLIENTE
        </TableCell>
        <TableCell bg="gray.200" textAlign="center">
          {formatPaymentTerm(
            purchaseOrder.firstExchangeClientTerm,
            purchaseOrder.firstExchangeClientTermDays
          )}
        </TableCell>
        <TableCell bg="gray.200" textAlign="center">
          {formatPaymentTerm(
            purchaseOrder.secondExchangeClientTerm,
            purchaseOrder.secondExchangeClientTermDays
          )}
        </TableCell>
        <TableCell bg="gray.200" textAlign="center">
          {formatPaymentTerm(
            purchaseOrder.nationalizationClientTerm,
            purchaseOrder.nationalizationClientTermDays
          )}
        </TableCell>
        <TableCell>
          {formatterUsd.format(mockData.firstExchangeClient)}
        </TableCell>
        <TableCell>
          {formatterUsd.format(mockData.secondExchangeClient)}
        </TableCell>
        <TableCell>
          {formatterUsd.format(mockData.nationalizationClient)}
        </TableCell>
      </Grid>

      {/* Cash flow section */}
      <Grid
        templateColumns="repeat(4, 1fr)"
        gap={0}
        mb="0"
        border="1px solid black"
      >
        <HeaderCell colSpan={8}>FLUXO DE CAIXA OPERAÇÃO</HeaderCell>
      </Grid>

      <Grid
        templateColumns="repeat(4, 1fr)"
        gap={0}
        mb="8px"
        border="1px solid black"
        borderTop={0}
      >
        <TableCell bg="gray.200"></TableCell>
        <TableCell bg="gray.200" textAlign="center">
          DESEMBOLSO
        </TableCell>
        <TableCell bg="gray.200" textAlign="center">
          PRAZO EM ABERTO
        </TableCell>
        <TableCell bg="gray.200" textAlign="center">
          DATA PREVISTA
        </TableCell>
        <TableCell bg="gray.200" textAlign="center">
          1º CÂMBIO
        </TableCell>
        <TableCell>
          {formatterUsd.format(mockData.firstExchangeDisbursement)}
        </TableCell>
        <TableCell textAlign="center">
          {mockData.firstExchangeOpenTerm} dias
        </TableCell>
        <TableCell textAlign="center">
          {formatDate(mockData.firstExchangeExpectedDate)}
        </TableCell>
        <TableCell bg="gray.200" textAlign="center">
          2º CÂMBIO
        </TableCell>
        <TableCell>
          {formatterUsd.format(mockData.secondExchangeDisbursement)}
        </TableCell>
        <TableCell textAlign="center">
          {mockData.secondExchangeOpenTerm} dias
        </TableCell>
        <TableCell textAlign="center">
          {formatDate(mockData.secondExchangeExpectedDate)}
        </TableCell>
        <TableCell bg="gray.200" textAlign="center">
          DESEMBARAÇO
        </TableCell>
        <TableCell>
          {formatterUsd.format(mockData.clearanceDisbursement)}
        </TableCell>
        <TableCell textAlign="center">
          {mockData.clearanceOpenTerm} dias
        </TableCell>
        <TableCell textAlign="center">
          {formatDate(mockData.clearanceExpectedDate)}
        </TableCell>
        <TableCell bg="gray.200" rowSpan={2} textAlign="center" pt="12px">
          VALOR NOTA FISCAL
        </TableCell>
        <TableCell bg="gray.200" textAlign="center">
          DOLAR
        </TableCell>
        <TableCell bg="gray.200" textAlign="center">
          REAL
        </TableCell>
        <TableCell bg="gray.200" textAlign="center">
          DATA PREVISTA
        </TableCell>
        <TableCell>
          {formatterUsd.format(mockData.invoiceValueDollar)}
        </TableCell>
        <TableCell>{formatterBrl.format(mockData.invoiceValueReal)}</TableCell>
        <TableCell textAlign="center">
          {formatDate(mockData.invoiceExpectedDate)}
        </TableCell>
      </Grid>

      {/* Signature lines */}
      <Grid justifyItems="flex-end" templateColumns="repeat(2, 1fr)" gap="4px">
        <SignatureLine name="Conrado Alexandre Lehmkuhl" />
        <SignatureLine name="Victor Hugo Caron Lehmkuhl" />
        <SignatureLine name="Gilberto Valin" />
        <SignatureLine name="Cintia C. Marx Jubelli" />
      </Grid>

      {/* Operational completion section */}
      <Grid
        templateColumns="repeat(4, 1fr)"
        gap={0}
        mb="0"
        mt="120px"
        border="1px solid black"
      >
        <HeaderCell colSpan={8}>
          PARA PREENCHIMENTO OPERACIONAL - INFNC (RO)
        </HeaderCell>
      </Grid>

      <Grid
        templateColumns="repeat(4, 1fr)"
        gap={0}
        mb="0"
        border="1px solid black"
        borderTop={0}
        borderBottom={0}
      >
        <TableCell bg="gray.200">FRETE ALL IN</TableCell>
        <TableCell>{formatterUsd.format(mockData.freightAllIn)}</TableCell>
        <TableCell bg="gray.200" textAlign="center">
          FRETE TIME
        </TableCell>
        <TableCell textAlign="center">{mockData.freeTimeDays} DIAS</TableCell>
      </Grid>

      <Grid
        templateColumns="repeat(8, 1fr)"
        gap={0}
        mb="8px"
        border="1px solid black"
        borderTop={0}
      >
        <TableCell colSpan={2} bg="gray.200">
          EMBARQUE E CHEGADA
        </TableCell>
        <TableCell textAlign="center" colSpan={3}>
          {formatDate(mockData.shipmentDate)}
        </TableCell>
        <TableCell textAlign="center" colSpan={3}>
          {formatDate(mockData.arrivalDate)}
        </TableCell>
      </Grid>

      {/* Observations */}
      <Box mb="16px">
        <Text fontWeight="bold" mb="4px" fontSize={"13px"}>
          OBSERVAÇÕES:
        </Text>
        <Box minH="18px" p="4px" fontSize={"13px"}>
          <Text>{mockData.observations}</Text>
        </Box>
      </Box>
    </Box>
  );
};

export default PurchaseOrderPrint;
